// Routes create form -- extracted from LogisticsRoutesPanel.tsx as the
// file-size-hygiene preamble for D3d-part-2. The form takes the hook
// return model and drives `m.submitCreate()` / `m.cancelCreate()`; the
// orchestrator decides when to render this by flipping `m.creating`
// (via "+ New route" button -> bumps newSignal -> hook sets creating).
import { SectionCard } from "@/components/common/SectionCard";
import {
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
} from "./logisticsRoutesShared";
import { unitHandoffOptionLabel } from "@/lib/ops/handoffIdentity";
import type { useLogisticsRoutesPanel } from "./useLogisticsRoutesPanel";

type Props = {
  m: ReturnType<typeof useLogisticsRoutesPanel>;
};

export function LogisticsRoutesCreateForm({ m }: Props) {
  return (
    <SectionCard
      title="Delivery routes"
      description={
        "A route is a per-unit delivery plan with ordered stops (plant origin → dealer holding lots → " +
        "end customer). One route = one unit by design; shared trucks are rows sharing carrier + " +
        "scheduled time. Carriers are optional at create time (\"carrier-tentative\") so you can line " +
        "up the unit + stops before a hauler is locked in. Use the route detail card below to add stops " +
        "and walk the state machine."
      }
    >
      {m.creating ? (
        <div className="space-y-2">
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Unit
            <select
              className={`${inputClass} mt-1`}
              value={m.newUnitId}
              onChange={(e) => m.setNewUnitId(e.target.value)}
            >
              <option value="">(pick a unit)</option>
              {m.units.map((u) => (
                <option key={u.id} value={u.id}>
                  {unitHandoffOptionLabel(u)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Carrier (optional — leave blank for carrier-tentative)
            <select
              className={`${inputClass} mt-1`}
              value={m.newCarrierId}
              onChange={(e) => m.setNewCarrierId(e.target.value)}
            >
              <option value="">(no carrier yet)</option>
              {m.carriers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Rig (optional — pick a fleet rig once a carrier is selected)
            <select
              className={`${inputClass} mt-1`}
              value={m.newRigId}
              onChange={(e) => m.setNewRigId(e.target.value)}
              disabled={!m.newCarrierId || m.createRigsLoading}
            >
              <option value="">
                {m.newCarrierId
                  ? m.createRigsLoading
                    ? "(loading rigs…)"
                    : m.createCarrierRigs.length === 0
                      ? "(carrier has no active rigs — leave blank)"
                      : "(no rig assigned yet)"
                  : "(pick a carrier first)"}
              </option>
              {m.createCarrierRigs.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.designation}
                  {r.standard_driver_name ? ` — ${r.standard_driver_name}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Driver (optional — auto-fills from rig&apos;s standard driver, freely overrideable)
            <input
              className={`${inputClass} mt-1`}
              value={m.newDriverName}
              onChange={(e) => m.setNewDriverName(e.target.value)}
              placeholder="Driver name for this run"
            />
          </label>
          {/* D3k: scheduled-for picker. Date picker + window-mode select +
              conditional time pickers. Empty date = "no schedule yet" (the
              route sits in `planned` with no scheduled_for, same as today's
              blank-field behavior). For Morning / Afternoon the canonical
              timestamp anchors at 9 / 13:00 local; for Specific window the
              start time is the canonical timestamp; for Specific time the
              time field IS the canonical timestamp; for Date only the
              canonical timestamp anchors at 08:00 local. */}
          <div className="rounded-lg border border-gray-200 dark:border-zinc-700 p-2 space-y-2">
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Scheduled date (leave blank for no schedule)
              <input
                type="date"
                className={`${inputClass} mt-1`}
                value={m.newScheduleDate}
                onChange={(e) => m.setNewScheduleDate(e.target.value)}
              />
            </label>
            {m.newScheduleDate ? (
              <>
                <label className="block text-xs text-gray-600 dark:text-zinc-300">
                  Time block
                  <select
                    className={`${inputClass} mt-1`}
                    value={m.newScheduleMode}
                    onChange={(e) => m.setNewScheduleMode(e.target.value as typeof m.newScheduleMode)}
                  >
                    <option value="date_only">Date only — no time set</option>
                    <option value="morning">Morning</option>
                    <option value="afternoon">Afternoon</option>
                    <option value="specific_window">Specific window (e.g. 8–10am)</option>
                    <option value="specific_time">Specific time (single target)</option>
                  </select>
                </label>
                {m.newScheduleMode === "specific_time" ? (
                  <label className="block text-xs text-gray-600 dark:text-zinc-300">
                    Target time
                    <input
                      type="time"
                      className={`${inputClass} mt-1`}
                      value={m.newScheduleStartTime}
                      onChange={(e) => m.setNewScheduleStartTime(e.target.value)}
                    />
                  </label>
                ) : null}
                {m.newScheduleMode === "specific_window" ? (
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-xs text-gray-600 dark:text-zinc-300">
                      Start time
                      <input
                        type="time"
                        className={`${inputClass} mt-1`}
                        value={m.newScheduleStartTime}
                        onChange={(e) => m.setNewScheduleStartTime(e.target.value)}
                      />
                    </label>
                    <label className="block text-xs text-gray-600 dark:text-zinc-300">
                      End time
                      <input
                        type="time"
                        className={`${inputClass} mt-1`}
                        value={m.newScheduleEndTime}
                        onChange={(e) => m.setNewScheduleEndTime(e.target.value)}
                      />
                    </label>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Notes
            <input
              className={`${inputClass} mt-1`}
              value={m.newNotes}
              onChange={(e) => m.setNewNotes(e.target.value)}
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              className={primaryBtnClass}
              onClick={() => void m.submitCreate()}
              disabled={m.actionBusy}
            >
              {m.actionBusy ? "Saving…" : "Create route"}
            </button>
            <button type="button" className={ghostBtnClass} onClick={m.cancelCreate}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="text-xs text-gray-500 dark:text-zinc-400">
          Use + New route above to schedule a delivery for one of the units you&apos;ve already created.
        </div>
      )}
    </SectionCard>
  );
}
